import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../lib/api';
import i18n from '../i18n';
import { trackCompleteRegistration, trackLead, trackLogin, sendServerEvent } from '../lib/metaPixel';

const AuthContext = createContext(null);

// Decode JWT to get expiration time (without verification)
const getTokenExpiration = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000; // Convert to milliseconds
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);
  const isRefreshingRef = useRef(false);

  // Proactive token refresh function
  const refreshToken = useCallback(async () => {
    if (isRefreshingRef.current) return; // Prevent multiple simultaneous refreshes
    
    const currentRefreshToken = localStorage.getItem('refresh_token');
    if (!currentRefreshToken) return;
    
    isRefreshingRef.current = true;
    console.log('[Auth] Proactive token refresh started');
    
    try {
      const response = await authAPI.refresh(currentRefreshToken);
      const { access_token, refresh_token: newRefreshToken } = response.data;
      
      localStorage.setItem('token', access_token);
      if (newRefreshToken) {
        localStorage.setItem('refresh_token', newRefreshToken);
      }
      
      console.log('[Auth] Token refreshed successfully');
      
      // Schedule next refresh
      scheduleTokenRefresh(access_token);
    } catch (error) {
      console.error('[Auth] Proactive refresh failed:', error);
      // Don't logout here - let the normal 401 handler deal with it
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  // Schedule token refresh 5 minutes before expiration
  const scheduleTokenRefresh = useCallback((token) => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    
    if (!token) return;
    
    const expiration = getTokenExpiration(token);
    if (!expiration) return;
    
    const now = Date.now();
    const timeUntilExpiry = expiration - now;
    const refreshTime = timeUntilExpiry - (5 * 60 * 1000); // 5 minutes before expiry
    
    if (refreshTime > 0) {
      console.log(`[Auth] Token refresh scheduled in ${Math.round(refreshTime / 60000)} minutes`);
      refreshTimerRef.current = setTimeout(() => {
        refreshToken();
      }, refreshTime);
    } else if (timeUntilExpiry > 30000) {
      // Token expires in less than 5 minutes but more than 30 seconds - refresh now
      console.log('[Auth] Token expiring soon, refreshing now');
      refreshToken();
    }
  }, [refreshToken]);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const savedUser = localStorage.getItem('user');
      
      if (token && savedUser) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          
          // Sync language
          if (userData.language) {
            i18n.changeLanguage(userData.language);
            localStorage.setItem('language', userData.language);
          }
          
          // Check if token is expired or about to expire
          const expiration = getTokenExpiration(token);
          if (expiration && expiration - Date.now() < 60000) {
            // Token expires in less than 1 minute - refresh immediately
            console.log('[Auth] Token expired or expiring, refreshing...');
            await refreshToken();
          }
          
          // Verify token with backend
          const response = await authAPI.getMe();
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
          
          // Schedule proactive refresh
          const currentToken = localStorage.getItem('token');
          scheduleTokenRefresh(currentToken);
        } catch (error) {
          console.error('Auth init error:', error);
          // Clear all auth data
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('refresh_token');
          setUser(null);
        }
      }
      
      setLoading(false);
    };

    initAuth();
    
    // Cleanup timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [refreshToken, scheduleTokenRefresh]);

  // Login - POST /api/auth/login
  const login = async (email, password) => {
    const response = await authAPI.login({ email, password });
    const { access_token, refresh_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refresh_token', refresh_token);
    }
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Schedule proactive refresh for new token
    scheduleTokenRefresh(access_token);
    
    // Priority: localStorage (user's UI choice) > userData (server) > default
    const userLang = localStorage.getItem('language') || userData.language || 'en';
    await i18n.changeLanguage(userLang);
    localStorage.setItem('language', userLang);
    
    // Track Login event (Meta Pixel)
    trackLogin({ plan: userData.plan });
    
    return userData;
  };

  // Register - POST /api/auth/register
  const register = async (name, email, password, language = 'it') => {
    const response = await authAPI.register({ name, email, password, language });
    const { access_token, refresh_token, user: userData, message } = response.data;
    
    // If email confirmation required
    if (!access_token && message) {
      return { 
        needsConfirmation: true, 
        message: message
      };
    }
    
    localStorage.setItem('token', access_token);
    if (refresh_token) {
      localStorage.setItem('refresh_token', refresh_token);
    }
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // Schedule proactive refresh for new token
    scheduleTokenRefresh(access_token);
    
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
    
    // Track registration event (Meta Pixel + Conversion API)
    const eventId = trackCompleteRegistration({ email_hash: email });
    if (eventId) {
      sendServerEvent('CompleteRegistration', { content_name: 'SmartBalloon Registration' }, eventId, email);
    }
    
    // Track Lead (Trial Activation) if user is on trial
    if (userData.plan === 'AI_TRIAL') {
      const leadEventId = trackLead({ trial_days: 7 });
      if (leadEventId) {
        sendServerEvent('Lead', { content_name: 'SmartBalloon Trial', content_category: '7-Day AI Trial' }, leadEventId, email);
      }
    }
    
    return userData;
  };

  const logout = () => {
    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const changeLanguage = async (language) => {
    // Always change UI language immediately
    i18n.changeLanguage(language);
    localStorage.setItem('language', language);
    updateUser({ language });
    
    // Try to sync with backend (non-blocking)
    try {
      await authAPI.updateLanguage(language);
    } catch (error) {
      console.error('Failed to sync language with server:', error);
      // Language is already changed locally, no need to block
    }
  };

  // Refresh user data from backend (useful after subscription changes)
  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      console.log('[Auth] User data refreshed, plan:', response.data.plan);
    } catch (error) {
      console.error('[Auth] Failed to refresh user:', error);
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register,
      logout, 
      updateUser,
      changeLanguage,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
